import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MediaPickerComponent,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkflowQuestionType,
  newGUID,
} from '@gripp/shared-logic';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../../../themes';
import { DragList } from '../../../dragList/dragList';
import { RoutineAdminInputText } from '../../components/routineAdminInputText';
import { AddQuestion } from './addQuestion';
import { DuplicateQuestion } from './duplicateQuestion';
import { WorkflowAdminQuestion } from './workflowAdminQuestion';

type WorkflowAdminPageProps = {
  page: WorkflowPageConfig;
  index: number;
  control: Control;
  mediaPicker?: MediaPickerComponent;
};

export const WorkflowAdminPage = (props: WorkflowAdminPageProps) => {
  const { t } = useTranslation();
  const { index, control } = props;
  const indexPrefix = `pages.${index}`;
  const i18nPrefix = 'routines.workflow.admin.page';
  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
    move: moveQuestion,
    update: updateQuestion,
    insert: insertQuestion,
  } = useFieldArray({
    control: control,
    name: `${indexPrefix}.questions`,
  });
  const watchedQuestions = useWatch({
    control,
    name: `${indexPrefix}.questions`,
  });

  const DefaultQuestion: WorkflowQuestionConfig = {
    id: newGUID(),
    name: `Question ${questionFields.length + 1}`,
    order: questionFields.length + 1,
    type: WorkflowQuestionType.Text,
    required: false,
    instructions: '',
  };

  const onAddQuestion = (type: WorkflowQuestionType, index: number) => {
    const newQuestion = {
      ...DefaultQuestion,
      order: index + 1,
      name: `Question ${index + 1}`,
    };
    insertQuestion(index + 1, {
      ...newQuestion,
      type,
    });
  };

  const onAppendQuestion = (type: WorkflowQuestionType) => {
    appendQuestion({
      ...DefaultQuestion,
      type,
    });
  };

  const onDuplicateQuestion = (questionIndex: number) => {
    const original = watchedQuestions?.[questionIndex];
    if (!original) return;
    const originalQuestion = original as WorkflowQuestionConfig;
    const duplicatedQuestion: WorkflowQuestionConfig = {
      ...originalQuestion,
      id: newGUID(),
      order: questionIndex + 2,
      name: `Question ${questionIndex + 2}`,
      isHeadline: false,
    };

    insertQuestion(questionIndex + 1, duplicatedQuestion);
  };

  const onDeleteQuestion = (questionIndex: number) => {
    removeQuestion(questionIndex);
  };

  const onReorderedFromTo = (fromIndex: number, toIndex: number) => {
    moveQuestion(fromIndex, toIndex);
  };

  const renderItem = (question: any) => {
    const indexInFields = questionFields.findIndex((q) => q.id === question.id);
    return (
      <View style={styles.questionContainer}>
        <View style={styles.questionActions}>
          <Pressable
            style={styles.delete}
            onPress={() => onDeleteQuestion(indexInFields)}
          >
            <MaterialCommunityIcons
              name="close-circle-outline"
              color={Colors.primary}
              size={24}
            />
          </Pressable>
        </View>
        <WorkflowAdminQuestion
          key={`question_${question.id}`}
          control={control}
          index={indexInFields}
          question={question}
          type={question.type}
          pagePrefix={indexPrefix}
          mediaPicker={props.mediaPicker}
        />
        <DuplicateQuestion
          i18nPrefix={i18nPrefix}
          onPress={() => onDuplicateQuestion(indexInFields)}
        />
        <AddQuestion
          i18nPrefix={i18nPrefix}
          onPress={(type) => onAddQuestion(type, indexInFields)}
        />
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Text style={styles.header}>
        {t(`${i18nPrefix}.page`, { page: index + 1 })}
      </Text>

      <RoutineAdminInputText
        control={props.control}
        i18nPrefix={i18nPrefix}
        name="instructions"
        indexPrefix={indexPrefix}
        textArea={true}
      />
      <View style={styles.questions}>
        <DragList
          list={questionFields}
          pivotKey={'id'}
          sortKey={'order'}
          renderCustomItem={renderItem}
          onReorderedFromTo={onReorderedFromTo}
          customItemStyle={styles.question}
        />
        {questionFields.length === 0 && (
          <AddQuestion i18nPrefix={i18nPrefix} onPress={onAppendQuestion} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: Colors.black,
    paddingHorizontal: 10,
  },
  header: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 20,
  },
  questions: {
    marginBottom: 20,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    paddingTop: 10,
  },
  question: {
    height: '100%',
  },
  questionContainer: {
    flex: 1,
    maxWidth: '80%',
    padding: 12,
  },
  questionActions: {
    alignSelf: 'flex-end',
    flexDirection: 'row',
    gap: 4,
  },
  delete: {
    justifyContent: 'center',
  },
});
